import React from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowUpRight, MapPin, ChevronRight, Home, CheckCircle2 } from "lucide-react";
import Seo from "@/components/Seo";
import PageShell from "@/components/PageShell";
import { CITIES, MACHINES } from "@/lib/pages-data";
import { BRAND } from "@/lib/brand";

const SITE = "https://thecoventures.com";

export default function CityPage() {
  const { city } = useParams();
  const c = CITIES[city];
  if (!c) return <Navigate to="/vending-machines" replace />;

  const canonical = `${SITE}/vending-machines/${c.slug}`;
  const title = c.metaTitle;
  const description = c.metaDescription;

  const localBusinessSchema = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    name: `${BRAND.name} — Vending Machines in ${c.name}`,
    image: "https://customer-assets.emergentagent.com/job_vend-modern/artifacts/bzkasv26_Logo.png",
    telephone: BRAND.phone,
    email: BRAND.email,
    url: canonical,
    priceRange: "₹₹",
    address: {
      "@type": "PostalAddress",
      addressLocality: c.name,
      addressRegion: c.state,
      addressCountry: "IN",
    },
    areaServed: { "@type": "City", name: c.name },
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: SITE + "/" },
      { "@type": "ListItem", position: 2, name: "Cities", item: SITE + "/vending-machines" },
      { "@type": "ListItem", position: 3, name: c.name, item: canonical },
    ],
  };

  return (
    <PageShell>
      <Seo
        title={title}
        description={description}
        canonical={canonical}
        keywords={c.keyword}
        jsonLd={[localBusinessSchema, breadcrumbSchema]}
      />

      <section className="max-w-7xl mx-auto px-6 md:px-10 py-8 md:py-12">
        <nav
          aria-label="Breadcrumb"
          className="flex items-center gap-1.5 text-xs text-zinc-500 mb-6"
          data-testid="city-breadcrumbs"
        >
          <Link to="/" className="hover:text-zinc-900 inline-flex items-center gap-1">
            <Home className="w-3.5 h-3.5" /> Home
          </Link>
          <ChevronRight className="w-3 h-3" />
          <Link to="/vending-machines" className="hover:text-zinc-900">Cities</Link>
          <ChevronRight className="w-3 h-3" />
          <span className="text-zinc-900">{c.name}</span>
        </nav>

        <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] text-emerald-700 bg-emerald-50 border border-emerald-100 rounded-full px-3 py-1.5 mb-5">
          <MapPin className="w-3.5 h-3.5" /> {c.name}, {c.state}
        </div>
        <h1
          data-testid="city-h1"
          className="font-display text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tighter text-zinc-950 leading-[1.02] max-w-3xl"
        >
          Vending machines in {c.name}.
        </h1>
        <p className="mt-5 text-zinc-600 text-base md:text-lg max-w-2xl">
          Buy smart, cashless vending machines for {c.name} offices, IT parks,
          factories and hospitals. UPI ready, telemetry-enabled and backed by
          Thecoventures&apos; pan-India AMC network.
        </p>

        <div className="mt-8 flex flex-wrap items-center gap-3">
          <Link
            to="/#contact"
            data-testid="city-cta-quote"
            className="inline-flex items-center gap-2 bg-emerald-500 hover:bg-emerald-600 text-white font-semibold rounded-full px-6 py-3 shadow-[0_10px_24px_rgba(16,185,129,0.3)] transition"
          >
            Get a {c.name} quote <ArrowUpRight className="w-4 h-4" />
          </Link>
          <a
            href={`tel:${BRAND.phone.replace(/\s/g, "")}`}
            className="inline-flex items-center gap-2 bg-white hover:bg-zinc-50 text-zinc-900 font-semibold rounded-full px-5 py-3 border border-zinc-200 transition"
          >
            Call {BRAND.phone}
          </a>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 md:px-10 py-8 md:py-12">
        <div className="text-[11px] uppercase tracking-[0.25em] text-emerald-700 mb-3">
          Local coverage
        </div>
        <h2 className="font-display text-3xl md:text-4xl font-semibold tracking-tight text-zinc-950 max-w-2xl">
          Where we deploy across {c.name}
        </h2>
        <ul className="mt-6 grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {c.highlights.map((h) => (
            <li
              key={h}
              className="flex items-start gap-3 text-zinc-700 rounded-2xl border border-zinc-200 bg-white p-4"
            >
              <CheckCircle2 className="w-5 h-5 text-emerald-500 shrink-0 mt-0.5" />
              <span className="text-sm">{h}</span>
            </li>
          ))}
        </ul>
      </section>

      <section className="max-w-7xl mx-auto px-6 md:px-10 py-8 md:py-14">
        <div className="text-[11px] uppercase tracking-[0.25em] text-amber-600 mb-3">
          Machines available in {c.name}
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {Object.values(MACHINES).map((m) => (
            <Link
              key={m.slug}
              to={`/products/${m.slug}`}
              data-testid={`city-machine-${m.slug}`}
              className="rounded-2xl border border-zinc-200 bg-white p-5 hover:border-emerald-300 hover:bg-emerald-50/40 transition"
            >
              <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">{m.tag}</div>
              <div className="mt-1.5 font-display text-lg font-semibold text-zinc-950">
                {m.name}
              </div>
              <div className="mt-2 text-xs text-zinc-600 line-clamp-2">{m.subh}</div>
            </Link>
          ))}
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 md:px-10 py-8 md:py-16">
        <div className="text-[11px] uppercase tracking-[0.25em] text-emerald-700 mb-3">
          Other cities we cover
        </div>
        <div className="flex flex-wrap gap-2">
          {Object.values(CITIES)
            .filter((x) => x.slug !== c.slug)
            .map((x) => (
              <Link
                key={x.slug}
                to={`/vending-machines/${x.slug}`}
                data-testid={`related-city-${x.slug}`}
                className="text-sm text-zinc-700 hover:text-emerald-700 hover:border-emerald-300 border border-zinc-200 rounded-full px-4 py-1.5 bg-white transition"
              >
                {x.name}
              </Link>
            ))}
        </div>
      </section>
    </PageShell>
  );
}
